import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  Collapse,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import image1 from '../../assets/riyal.png';

/**
 * Component for displaying debit note summary and totals
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Debit note items
 * @param {string} props.additionalCost - Additional cost
 * @param {string} props.discountPercentage - Discount percentage
 * @param {string} props.discountAmount - Discount amount
 * @param {Object} props.referenceInvoice - Reference invoice data
 * @param {number} props.referenceInvoice.amount - Reference invoice amount
 * @param {Function} props.calculateSubtotal - Function to calculate subtotal
 * @param {Function} props.calculateVat - Function to calculate VAT
 * @param {Function} props.calculateTotal - Function to calculate total
 * @param {Function} props.getTotalPayments - Function to get total payments
 * @param {Function} props.getRemainingBalance - Function to get remaining balance
 * @param {Function} props.formatCurrency - Function to format currency values
 */
const DebitNoteSummary = ({
  items = [],
  additionalCost = 0,
  discountPercentage = 0,
  discountAmount = 0,
  referenceInvoice = { amount: 0 },
  calculateSubtotal,
  calculateVat,
  calculateTotal,
  getTotalPayments,
  getRemainingBalance,
  formatCurrency = (val) => (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <img src={image1} alt="SAR" style={{ width: '13px', height: '13px', marginRight: '4px' }} />
      {parseFloat(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
    </span>
  )
}) => {
  // Calculate totals for summary display
  const subtotal = calculateSubtotal(items);
  const refInvoiceAmount = parseFloat(referenceInvoice.amount || 0);
  const additionalCostValue = parseFloat(additionalCost || 0);
  const discountAmountValue = parseFloat(discountAmount || 0);
  
  // Calculate percentages against reference invoice amount
  const additionalCostPercentage = refInvoiceAmount ? ((additionalCostValue / refInvoiceAmount) * 100).toFixed(2) : 0;
  const discountPercentageValue = refInvoiceAmount ? ((discountAmountValue / refInvoiceAmount) * 100).toFixed(2) : 0;
  
  const netAmount = subtotal + additionalCostValue - discountAmountValue;
  const vatAmount = calculateVat(items);
  const grandTotal = calculateTotal(items);
  const totalPaid = getTotalPayments ? getTotalPayments() : 0;
  const remainingBalance = getRemainingBalance ? getRemainingBalance() : grandTotal;
  
  // Dummy payment status to fix runtime error
  const paymentStatus = { label: 'Not Paid', color: 'error' };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 1.5, 
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#f8faff',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalculateIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Summary
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Collapse in={true} timeout="auto">
        <Box sx={{ p: 2 }}>
          <Box 
            sx={{ 
              p: 2, 
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.09)'
            }}
          >
            <Stack spacing={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  Subtotal
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(subtotal)}
                </Typography>
              </Box>
              

              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  Discount
                  {discountAmountValue > 0 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                      ({discountPercentageValue}% of {formatCurrency(refInvoiceAmount)})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(discountAmountValue)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)', backgroundColor: '#f9f9f9' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  Net Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(netAmount)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  VAT Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(vatAmount)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1.5, backgroundColor: '#f5f9ff', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  Total Amount
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(grandTotal)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DebitNoteSummary;
